// What? A React component displaying all section prompts for a document type.
// Why? To allow users to review and copy all section prompts at once.
// How? Modal that fetches and displays section prompts with copy functionality.

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";

import { DocumentTypeSection } from "../../../types/documentTypes";
import { useDocumentTypeSections } from "../../../hooks/useDocumentTypeSections";
import Loading from "../../../../../components/Loading";

interface SectionPromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTypeId: number;
  workspace_id: string;
}

const SectionPromptsModal: React.FC<SectionPromptsModalProps> = ({
  isOpen,
  onClose,
  documentTypeId,
  workspace_id,
}) => {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [sections, setSections] = useState<DocumentTypeSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getSections } = useDocumentTypeSections(workspace_id);

  useEffect(() => {
    let mounted = true;

    const fetchSections = async () => {
      if (!documentTypeId) return;

      try {
        setLoading(true);
        setError(null);
        const fetchedSections = await getSections(documentTypeId);
        if (mounted) {
          setSections(fetchedSections);
        }
      } catch (error) {
        console.error("Error fetching sections:", error);
        if (mounted) {
          setError("Failed to load section prompts");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (isOpen && !sections.length) {
      fetchSections();
    }

    return () => {
      mounted = false;
    };
  }, [isOpen, documentTypeId, getSections, sections.length]);

  const formatSectionPrompts = () => {
    return sections
      .map((section) => {
        const defaultPrompt = section.prompts?.find((p) => p.IsDefault);
        return `Section: ${section.Title}\nPrompt: ${defaultPrompt?.PromptText || "No prompt defined"}\n\n`;
      })
      .join("");
  };

  const formatSectionPromptsForExport = () => {
    const lines: string[] = [
      "<!-- MERIDIAN_DOCUMENT_TYPE_EXPORT_V1 -->",
      "# Document Type Structure Export",
      "",
    ];

    // Recursive function to add section and its children
    const addSectionWithChildren = (
      section: DocumentTypeSection,
      level: number,
    ) => {
      const headingPrefix = "#".repeat(level + 1); // level 1 = ##, level 2 = ###, etc.
      const defaultPrompt = section.prompts?.find((p) => p.IsDefault);
      lines.push(`${headingPrefix} ${section.Title}`);
      lines.push(
        `**Prompt:** ${defaultPrompt?.PromptText || "No prompt defined"}`,
      );
      lines.push("");

      // Find and process children
      const children = sections.filter(
        (s) => s.ParentSectionID === section.DocumentTypeSectionID,
      );
      children.forEach((child) => {
        addSectionWithChildren(child, level + 1);
      });
    };

    // Start with root sections (level 1 = ##)
    const rootSections = sections.filter((s) => s.ParentSectionID === null);
    rootSections.forEach((root) => {
      addSectionWithChildren(root, 1);
    });

    return lines.join("\n");
  };

  const handleDownload = () => {
    const content = formatSectionPromptsForExport();
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `document-type-structure.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatSectionPrompts());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 800, width: "90vw", maxHeight: "80vh" }}>
        <DialogTitle>Section Prompts</DialogTitle>

        <Box
          sx={{
            overflow: "auto",
            flex: 1,
            py: 2,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading />
            </Box>
          ) : error ? (
            <Typography
              level="body-md"
              color="danger"
              sx={{ textAlign: "center", py: 4 }}
            >
              {error}
            </Typography>
          ) : sections.length === 0 ? (
            <Typography
              level="body-md"
              color="neutral"
              sx={{ textAlign: "center", py: 4 }}
            >
              No sections found for this document type.
            </Typography>
          ) : (
            <Stack spacing={0} divider={<Divider />}>
              {sections.map((section, index) => {
                const defaultPrompt = section.prompts?.find((p) => p.IsDefault);
                return (
                  <Box
                    key={section.DocumentTypeSectionID || index}
                    sx={{ py: 2 }}
                  >
                    <Typography level="title-md" sx={{ mb: 1.5 }}>
                      {section.Title}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        bgcolor: "background.level1",
                        borderRadius: "sm",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "code",
                        fontSize: "sm",
                        lineHeight: 1.5,
                        overflow: "auto",
                      }}
                    >
                      {defaultPrompt?.PromptText || "No prompt defined"}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 2 }}
        >
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<DownloadIcon />}
            onClick={handleDownload}
            disabled={sections.length === 0}
          >
            Download
          </Button>
          <Button
            variant="solid"
            color="primary"
            startDecorator={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            disabled={sections.length === 0}
          >
            {copySuccess ? "Copied!" : "Copy Prompts"}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default SectionPromptsModal;
